/**
 * Money in / money out.
 *
 * ON-RAMP (bank → card): the user's Gnosis Pay account gets a personal
 * Monerium IBAN; a plain bank transfer mints e-money (GBPe/EURe) directly
 * into the card Safe. Florin just activates it and shows the bank details.
 *
 * RESERVE TOP-UP (card → reserve): Gnosis Pay relays a Safe withdrawal that
 * Florin's wallet (a Safe owner, registered at onboarding) signs as EIP-712.
 * The card token (GBPe/EURe) arrives in the Florin wallet after the 3-minute
 * Delay-module cooldown, then CoW Protocol converts it to USDC.e — the only
 * venue with GBPe solver liquidity (verified live 2026-08-11).
 *
 * OFF-RAMP (investments → card): sell stock → USDG (4663), bridge back to
 * USDC.e (Gnosis) via LiFi, convert to the card token via CoW with the card
 * Safe as receiver — the proceeds land as spendable card balance. From the
 * Safe, the IBAN rails can take it back to a bank account.
 *
 * v1 note: these are run-while-open operations with progress callbacks; the
 * round-up engine's crash-proof resume does not cover them yet. A crash
 * mid-flow strands nothing — funds sit in the Florin wallet and the next
 * attempt picks them up — but the UI won't auto-resume. Logged in STATUS.md.
 */
import { erc20Abi } from "viem";
import * as gp from "./gnosispay/api";
import { executeCowSwap } from "./cow";
import { ensureDestGasForSell, ensureGnosisGas } from "./gas";
import { getLifiQuote, sendLifiTransaction, waitLifiDone } from "./lifi";
import { gnosisPublicClient, robinhoodPublicClient } from "./chains";
import { GNOSIS_CHAIN, GNOSIS_SPEND_TOKENS, LIFI, RESERVE_TOKEN, ROBINHOOD_CHAIN } from "./config";
import type { LocalWallet } from "./wallet";
import type { Hex } from "./types";

export type Progress = (label: string) => void;

// ---------- IBAN on-ramp ----------

export interface IbanState {
  available: boolean;
  details?: gp.IbanDetails;
}

export async function getIbanState(): Promise<IbanState> {
  const details = await gp.ibanDetails().catch(() => undefined);
  if (details && details.ibanStatus !== "NOTSTARTED") return { available: true, details };
  const available = await gp.ibanAvailable().catch(() => false);
  return { available, details };
}

/** Activate the personal IBAN (sign Monerium's ownership declaration). */
export async function activateIban(wallet: LocalWallet): Promise<gp.IbanDetails> {
  const message = await gp.ibanSigningMessage();
  const signature = await wallet.account.signMessage({ message });
  await gp.createMoneriumIntegration(signature);
  return gp.ibanDetails();
}

// ---------- reserve top-up from card balance ----------

function cardToken(tokenSymbol: string | null | undefined) {
  const t = GNOSIS_SPEND_TOKENS.find((x) => x.symbol === tokenSymbol);
  if (!t) throw new Error(`Unsupported card currency: ${tokenSymbol ?? "unknown"}`);
  return t;
}

async function tokenBalance(token: Hex, owner: Hex): Promise<bigint> {
  return gnosisPublicClient().readContract({
    address: token,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [owner],
  });
}

/**
 * Pull `amountMinor` (pence/cents of the CARD currency) from the card Safe
 * into the reserve. Steps: owner check → EIP-712 sign → Gnosis Pay relays
 * the delayed withdrawal → wait for arrival → CoW convert to USDC.e.
 */
export async function fundReserveFromCard(
  wallet: LocalWallet,
  amountMinor: number,
  onProgress: Progress,
): Promise<void> {
  const cfg = await gp.getSafeConfig();
  if (!cfg.isDeployed || !cfg.address) throw new Error("No card Safe deployed yet");
  const token = cardToken(cfg.tokenSymbol);
  const units = (BigInt(amountMinor) * 10n ** BigInt(token.decimals)) / 100n;

  const owners = await gp.getSafeOwners();
  if (!owners.some((o) => o.toLowerCase() === wallet.address.toLowerCase())) {
    throw new Error(
      `Florin's wallet is not a Safe owner (owners: ${owners.join(", ")}). ` +
        "Withdrawals must be signed by an owner — see STATUS.md.",
    );
  }

  onProgress("Requesting withdrawal…");
  const typed = await gp.getWithdrawTypedData(units, wallet.address, token.address);
  const signature = await wallet.account.signTypedData({
    domain: typed.domain,
    types: typed.types as Parameters<typeof wallet.account.signTypedData>[0]["types"],
    primaryType: typed.primaryType,
    message: typed.message as Record<string, unknown>,
  });
  const before = await tokenBalance(token.address, wallet.address);
  await gp.submitWithdraw({
    amountUnits: units,
    to: wallet.address,
    tokenAddress: token.address,
    signature,
    message: typed.message,
  });

  onProgress("Waiting for the 3-minute security delay…");
  const deadline = Date.now() + 12 * 60_000;
  while ((await tokenBalance(token.address, wallet.address)) < before + units) {
    if (Date.now() > deadline) {
      throw new Error("Withdrawal not arrived yet — it may still execute; check again shortly.");
    }
    await new Promise((r) => setTimeout(r, 10_000));
  }

  onProgress(`Converting ${token.symbol} → USDC.e…`);
  await executeCowSwap(wallet, token.address, RESERVE_TOKEN.address, units, wallet.address, onProgress);
  // Replenish Gnosis-side gas from the fresh deposit (self-sustaining after
  // the one-time sponsored bootstrap). Best-effort — next top-up retries.
  await ensureGnosisGas(wallet, onProgress).catch(() => undefined);
  onProgress("Reserve funded ✓");
}

// ---------- off-ramp: investments → card balance ----------

/**
 * Sell stock on Robinhood Chain, bridge back to Gnosis, convert to the card
 * currency with the card Safe as receiver. Proceeds become spendable card
 * balance (and can continue to a bank via the IBAN rails).
 */
export async function cashOutToCard(
  wallet: LocalWallet,
  stockSymbol: string,
  stockUnits: bigint,
  onProgress: Progress,
): Promise<void> {
  const cfg = await gp.getSafeConfig();
  if (!cfg.isDeployed || !cfg.address) throw new Error("No card Safe deployed yet");
  const token = cardToken(cfg.tokenSymbol);
  const stock = ROBINHOOD_CHAIN.stockTokens[stockSymbol];
  if (!stock) throw new Error(`Unknown stock token: ${stockSymbol}`);
  if (stockUnits <= 0n) throw new Error("Nothing to cash out");

  // Leg 1: sell stock → USDG on Robinhood Chain. The sell pays 4663 gas, so
  // make sure the tank isn't dry before quoting (refills from the reserve).
  await ensureDestGasForSell(wallet, onProgress);
  onProgress(`Selling ${stockSymbol}…`);
  const sellQuote = await getLifiQuote({
    fromChain: ROBINHOOD_CHAIN.id,
    toChain: ROBINHOOD_CHAIN.id,
    fromToken: stock.address,
    toToken: ROBINHOOD_CHAIN.usdg.address,
    fromAmount: stockUnits,
    fromAddress: wallet.address,
    slippage: LIFI.swapSlippage,
  });
  const sellTx = await sendLifiTransaction(wallet.account, sellQuote);
  const rcpt = await robinhoodPublicClient().waitForTransactionReceipt({
    hash: sellTx,
    timeout: LIFI.statusTimeoutMs,
  });
  if (rcpt.status !== "success") throw new Error("Sell transaction reverted");

  // Leg 2: bridge USDG → USDC.e on Gnosis.
  onProgress("Bridging back to Gnosis…");
  const usdgUnits = sellQuote.toAmountMin; // conservative; dust stays in USDG
  const bridgeQuote = await getLifiQuote({
    fromChain: ROBINHOOD_CHAIN.id,
    toChain: GNOSIS_CHAIN.id,
    fromToken: ROBINHOOD_CHAIN.usdg.address,
    toToken: RESERVE_TOKEN.address,
    fromAmount: usdgUnits,
    fromAddress: wallet.address,
    slippage: LIFI.bridgeSlippage,
  });
  const bridgeTx = await sendLifiTransaction(wallet.account, bridgeQuote);
  const done = await waitLifiDone(bridgeTx, ROBINHOOD_CHAIN.id, GNOSIS_CHAIN.id);
  const usdceUnits = done.receivedAmount ?? bridgeQuote.toAmountMin;

  // Leg 3: convert to the card currency, delivered straight into the Safe.
  onProgress(`Converting to ${token.symbol}…`);
  await executeCowSwap(
    wallet,
    RESERVE_TOKEN.address,
    token.address,
    usdceUnits,
    cfg.address as Hex,
    onProgress,
  );
  onProgress("Cashed out to your card ✓");
}

/** Send loose reserve USDC.e back to the card as well (smaller path). */
export async function reserveToCard(
  wallet: LocalWallet,
  usdceUnits: bigint,
  onProgress: Progress,
): Promise<void> {
  const cfg = await gp.getSafeConfig();
  if (!cfg.isDeployed || !cfg.address) throw new Error("No card Safe deployed yet");
  const token = cardToken(cfg.tokenSymbol);
  onProgress(`Converting to ${token.symbol}…`);
  await executeCowSwap(
    wallet,
    RESERVE_TOKEN.address,
    token.address,
    usdceUnits,
    cfg.address as Hex,
    onProgress,
  );
  onProgress("Sent to your card ✓");
}
